import type { Entity } from '@backstage/catalog-model';
import type { EntityContentGroupDefinitions } from '@backstage/plugin-catalog-react/alpha';
import type {
  BreadcrumbPartSibling,
  BreadcrumbPartType,
} from '@ipen-portal/plugin-navigation-react';
import type { ReactElement, ReactNode } from 'react';

import { getDisplayName } from '@ipen-portal/plugin-common';
import {
  isRouteWithMultipleTabs,
  isRouteWithSubPath,
  SubRouteContext,
} from '@ipen-portal/plugin-common-react';
import { BreadcrumbContext } from '@ipen-portal/plugin-navigation-react';
import { Typography } from '@mui/material';
import { useContext, useMemo } from 'react';
import { tss } from 'tss-react/mui';

import { PageHeaderToolbar } from '../PageHeaderToolbar';
import { Breadcrumbs } from './components/Breadcrumbs/Breadcrumbs';
import { EntityContextMenu } from './components/EntityContextMenu';

export const PAGE_HEADER_SIZE = 256;

const useStyles = tss.create(({ theme }) => ({
  background: {
    background: theme.page.backgroundImage,
    backgroundSize: 'cover',
    display: 'flex',
    flex: 0,
    flexDirection: 'row',
  },
  header: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(2),
    margin: '0 auto',
    width: '100%',
  },
  headerContainer: {
    display: 'grid',
    flex: 1,
    gap: theme.spacing(1),
    gridTemplateRows: 'minmax(24px, auto) 1fr minmax(48px, auto)',
    margin: '0 auto',
    maxWidth: 2560,
    minHeight: PAGE_HEADER_SIZE,
    padding: theme.spacing(3),
    [theme.breakpoints.up('xl')]: {
      padding: theme.spacing(3, 12),
    },
  },
  image: {
    maxWidth: 160,
    width: '100%',
  },
  logo: {
    alignItems: 'center',
    background: theme.palette.common.white,
    display: 'none',
    flexShrink: 0,
    justifyContent: 'center',
    padding: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
    width: PAGE_HEADER_SIZE,
  },
  subtitle: {
    '&::after': {
      content: '""',
      fontSize: 0,
      whiteSpace: 'pre',
    },
    'color': theme.palette.common.white,
    'fontSize': 24,
    'margin': 0,
  },
  title: {
    color: theme.palette.common.white,
    display: '-webkit-box',
    fontFamily: 'EONOffice',
    fontSize: 64,
    fontWeight: 'normal',
    lineClamp: 1,
    margin: 0,
    overflow: 'hidden',
    paddingLeft: 4, // required for the font to render correctly
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 1,
  },
}));

type Props = {
  entity?: Entity;
  groupDefinitions?: EntityContentGroupDefinitions;
  logo?: ReactElement | string;
  subtitle?: string;
  title: string;
  titleOrder?: Record<string, number>;
  toolbar?: ReactNode;
};

export const PageHeader = ({
  entity,
  groupDefinitions,
  logo,
  subtitle,
  title,
  titleOrder,
  toolbar,
}: Props) => {
  const { classes } = useStyles();
  const { breadcrumbs } = useContext(BreadcrumbContext);
  const { selectedSubPath, selectedSubRoute, subRoutes } =
    useContext(SubRouteContext);

  const siblings = useMemo(
    () =>
      subRoutes.flatMap(subRoute => {
        const nestedRoutes = isRouteWithMultipleTabs(subRoute)
          ? subRoute.tabs
          : [subRoute];
        return nestedRoutes.map(route => {
          let path = isRouteWithSubPath(route)
            ? `${subRoute.path}/${route.subPath}`
            : subRoute.path;

          // And remove leading / for relative navigation
          path = path.startsWith('ipenportal://')
            ? path.slice(13)
            : path.replace(/^\//, '');

          return {
            current:
              subRoute === selectedSubRoute
              && (!isRouteWithSubPath(route) || route.subPath === selectedSubPath),
            fullTitle: route.title,
            group: route.group,
            icon: route.icon,
            link: path,
            title: route.title,
          } satisfies BreadcrumbPartSibling;
        });
      }),
    [subRoutes, selectedSubRoute, selectedSubPath],
  );

  const extendedBreadcrumbs = useMemo(() => {
    if (!selectedSubRoute) {
      return breadcrumbs ?? [];
    }

    const current = siblings.find(sibling => sibling.current);
    let fullTitle = current?.title ?? '';
    if (current?.group) {
      const groupTitle = groupDefinitions?.[current?.group ?? '']?.title;
      if (groupTitle) {
        fullTitle = `${groupTitle} / ${fullTitle}`;
      }
    }
    const currentElement: BreadcrumbPartType = {
      fullTitle,
      link: current?.link,
      siblings,
      title: current?.title ?? '',
    };
    return [...(breadcrumbs ?? []), currentElement];
  }, [selectedSubRoute, siblings, breadcrumbs, groupDefinitions]);

  return (
    <div className={classes.background}>
      {logo && (
        <div className={classes.logo}>
          {typeof logo === 'string'
            ? (
                <img
                  alt={getDisplayName(entity)}
                  className={classes.image}
                  src={logo}
                />
              )
            : (
                logo
              )}
        </div>
      )}

      <section aria-label="Page Header" className={classes.headerContainer}>
        <Breadcrumbs
          groupDefinitions={groupDefinitions}
          parts={extendedBreadcrumbs}
          titleOrder={titleOrder}
        />

        <section aria-label="Page Title" className={classes.header}>
          <Typography className={classes.title} variant="h3">
            {title}
          </Typography>

          <Typography className={classes.subtitle} variant="h4">
            {subtitle}
          </Typography>
        </section>

        <PageHeaderToolbar>
          {toolbar}
          {entity && <EntityContextMenu entity={entity} />}
        </PageHeaderToolbar>
      </section>
    </div>
  );
};
